import { useEffect, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';
import { Warning } from '@/ui/icons/warning';

interface DriverAccountModalProps {
  visible?: boolean;
  onClose: () => void;
  onReconnect: () => void;
}

const ACCESS_LIMITATIONS = [
  'No Supercharging records',
  'No vehicle immobilization',
  'No remote unlock/lock for renters',
];

export function DriverAccountModal({
  visible = false,
  onClose,
  onReconnect,
}: DriverAccountModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleClose = () => {
    setShowModal(false);
    onClose?.();
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={styles.container}
      onBackdropPress={handleClose}
    >
      <View style={styles.shadowWrapper}>
        <View style={styles.containerView}>
          <View style={styles.header}>
            <Text type="title2">Driver Account Detected</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={handleClose}
            />
          </View>

          <View style={styles.content}>
            <View style={{ gap: 8 }}>
              <Icon
                type="naked"
                icon={<Warning />}
                fillColor={theme.colors.icon.attentionColor}
                width={48}
                height={24}
              />

              <Text type="title2" style={{ paddingTop: 10 }}>Reconnect With The Owner Account</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                {
                  "This car is missing access permissions. It needs to be reconnected via its owner's account to have full access."
                }
              </Text>
            </View>

            <View style={styles.limitationsCard}>
              {ACCESS_LIMITATIONS.map((limitation) => (
                <View key={limitation} style={styles.limitationRow}>
                  <Icon
                    type="naked"
                    icon={<Cancel />}
                    fillColor={theme.colors.text.secondary}
                    width={9}
                    height={9}
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary, marginLeft: 3 }}
                  >
                    {limitation}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.footer}>
            <Button
              text="Sign In With Owner Account"
              type="primary"
              onPress={() => {
                onReconnect();
                handleClose();
              }}
              style={styles.signInButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '95%',
      sm: 600,
    },
    width: {
      xs: '95%',
    },
  },
  shadowWrapper: {
    borderRadius: 16,
    boxShadow:
      '0px 5px 5px rgba(144, 148, 165, 0.1), 0px 14px 24px rgba(144, 148, 165, 0.12)',
  },
  containerView: {
    backgroundColor: theme.colors.background_1,
    borderWidth: 1,
    borderColor: theme.colors.dialog.borderColor,
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.dialog.borderColor,
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    gap: 16,
  },
  limitationsCard: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 8,
  },
  limitationRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: {
      xs: 12,
      md: 16,
    },
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.dialog.borderColor,
  },
  signInButton: {
    width: {
      xs: '100%',
      md: 'auto',
    },
    paddingHorizontal: {
      xs: 16,
      md: 24,
    },
  },
}));
